import React, { useCallback, useRef, useState, useEffect } from 'react';
import { Loader2, Maximize, Minus, Play, Plus } from 'lucide-react';
import { useDroppable } from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { IntegrationCanvasNode, IntegrationFlow, NodeExecutionStatus } from '../types';
import { IntegrationConnectionLines } from './IntegrationConnectionLines';

interface IntegrationCanvasProps {
  nodes: IntegrationCanvasNode[];
  flows: IntegrationFlow[];
  zoom: number;
  panX: number;
  panY: number;
  onZoomChange: (zoom: number) => void;
  onPanChange: (x: number, y: number) => void;
  onRecenter: (viewportWidth: number, viewportHeight: number) => void;
  isRunning?: boolean;
  executionStatus?: 'idle' | 'running' | 'completed' | 'failed';
  onStart?: () => void;
  canStart?: boolean;
  nodeStatuses?: Record<string, NodeExecutionStatus>;
  flowEnabled?: boolean;
  onToggleEnabled?: (enabled: boolean) => void;
  syncMode?: string;
  children?: React.ReactNode;
}

const ZOOM_STEP = 0.25;
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 2;

export const IntegrationCanvas: React.FC<IntegrationCanvasProps> = ({
  nodes,
  flows,
  zoom,
  panX,
  panY,
  onZoomChange,
  onPanChange,
  onRecenter,
  isRunning = false,
  executionStatus = 'idle',
  onStart,
  canStart = true,
  nodeStatuses,
  flowEnabled = false,
  onToggleEnabled,
  syncMode = 'manual',
  children,
}) => {
  const { t } = useTranslation();
  const viewportRef = useRef<HTMLDivElement>(null);
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

  const { setNodeRef, isOver } = useDroppable({ id: 'integration-canvas' });

  const setRefs = useCallback(
    (el: HTMLDivElement | null) => {
      (viewportRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
      setNodeRef(el);
    },
    [setNodeRef]
  );

  // Pan via left-click drag on background, middle-click, or Space+left-click
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // Middle-click, Space+click, or left-click on canvas background (nodes call stopPropagation)
      if (e.button === 1 || e.button === 0) {
        e.preventDefault();
        setIsPanning(true);
        panStartRef.current = { x: e.clientX, y: e.clientY, panX, panY };
      }
    },
    [panX, panY]
  );

  useEffect(() => {
    if (!isPanning) return;

    const handleMouseMove = (e: MouseEvent) => {
      const dx = e.clientX - panStartRef.current.x;
      const dy = e.clientY - panStartRef.current.y;
      onPanChange(panStartRef.current.panX + dx, panStartRef.current.panY + dy);
    };

    const handleMouseUp = () => setIsPanning(false);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isPanning, onPanChange]);

  const handleZoomIn = () => onZoomChange(Math.min(ZOOM_MAX, zoom + ZOOM_STEP));
  const handleZoomOut = () => onZoomChange(Math.max(ZOOM_MIN, zoom - ZOOM_STEP));
  const handleRecenter = () => {
    if (!viewportRef.current) return;
    const rect = viewportRef.current.getBoundingClientRect();
    onRecenter(rect.width, rect.height);
  };

  const canvasControls: Array<{ testId: string; label: string; onClick: () => void; icon: React.ReactNode }> = [
    { testId: 'canvas-zoom-in', label: t('PAGES.API_INTEGRATION.ZOOM_IN'), onClick: handleZoomIn, icon: <Plus className="size-4" /> },
    { testId: 'canvas-zoom-out', label: t('PAGES.API_INTEGRATION.ZOOM_OUT'), onClick: handleZoomOut, icon: <Minus className="size-4" /> },
    { testId: 'canvas-recenter', label: t('PAGES.API_INTEGRATION.RECENTER'), onClick: handleRecenter, icon: <Maximize className="size-4" /> },
  ];

  return (
    <div
      ref={setRefs}
      data-testid="integration-canvas"
      onMouseDown={handleMouseDown}
      className={cn(
        'relative h-full w-full overflow-hidden rounded-xl border bg-muted transition-colors',
        isOver && 'border-ring',
        isPanning ? 'cursor-grabbing' : 'cursor-grab',
      )}
    >
      {/* Canvas Controls — local provider: the canvas is rendered outside the app shell in tests */}
      <div data-testid="canvas-controls" className="absolute top-3 left-3 z-10 flex gap-1">
        <TooltipProvider>
        {canvasControls.map(control => (
          <Tooltip key={control.testId}>
            <TooltipTrigger asChild>
              <Button
                data-testid={control.testId}
                variant="outline"
                size="icon-sm"
                onClick={control.onClick}
                aria-label={control.label}
              >
                {control.icon}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{control.label}</TooltipContent>
          </Tooltip>
        ))}
        </TooltipProvider>
      </div>

      {/* Top-right controls: Enable toggle */}
      {nodes.length > 0 && (
        <div className="absolute top-3 right-3 z-10 flex items-center gap-2">
          {/* Enable / Disable toggle — only for scheduled mode */}
          {onToggleEnabled && syncMode === 'scheduled' && (
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border bg-background px-2 py-1">
              <span
                className={cn(
                  'text-xs font-medium',
                  flowEnabled ? 'text-success' : 'text-muted-foreground',
                )}
              >
                {flowEnabled ? t('PAGES.API_INTEGRATION.ENABLED') : t('PAGES.API_INTEGRATION.DISABLED')}
              </span>
              <Switch
                checked={flowEnabled}
                onCheckedChange={(checked) => onToggleEnabled(checked)}
              />
            </label>
          )}
        </div>
      )}

      {/* Transform Wrapper — canvas pixel space, LTR by design */}
      <div
        data-testid="canvas-transform-wrapper"
        className="absolute top-0 left-0 h-full w-full origin-top-left bg-[radial-gradient(circle,var(--border)_1px,transparent_1px)] bg-[length:20px_20px]"
        style={{ transform: `translate(${panX}px, ${panY}px) scale(${zoom})` }}
      >
        {/* Connection Lines */}
        <IntegrationConnectionLines flows={flows} nodeStatuses={nodeStatuses} isExecuting={isRunning} />

        {/* Nodes */}
        {children}

        {/* Start button — small circle above the Trigger node */}
        {onStart && (() => {
          const triggerNode = nodes.find(n => n.type === 'trigger');
          if (!triggerNode) return null;
          const triggerWidth = 104;
          const btnX = triggerNode.x + triggerWidth / 2;
          const btnY = triggerNode.y - 52;

          return (
            <div
              className="absolute z-10 flex -translate-x-1/2 flex-col items-center"
              style={{ left: btnX, top: btnY }}
            >
              {syncMode !== 'manual' && executionStatus === 'idle' ? (
                <span className="mb-1 whitespace-nowrap text-[8px] font-medium tracking-wide text-muted-foreground">
                  {t('PAGES.API_INTEGRATION.RUN_ONCE_NOTE', 'Runs once, then follows schedule')}
                </span>
              ) : (
                <span className="mb-1 h-3.5" />
              )}
              <button
                type="button"
                data-testid="canvas-start-button"
                onClick={onStart}
                disabled={isRunning || !canStart}
                className={cn(
                  'flex size-6 items-center justify-center rounded-full transition-all',
                  executionStatus === 'running'
                    ? 'bg-transparent text-foreground'
                    : 'bg-foreground text-background hover:brightness-90 disabled:opacity-40',
                )}
              >
                {executionStatus === 'running'
                  ? <Loader2 className="size-4 animate-spin" />
                  : <Play className="size-3.5" />}
              </button>
            </div>
          );
        })()}
      </div>

      {/* Empty state */}
      {nodes.length === 0 && (
        <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
          <p className="mb-1 text-base font-medium text-muted-foreground">
            {t('PAGES.API_INTEGRATION.EMPTY_CANVAS_TITLE')}
          </p>
          <p className="text-sm text-muted-foreground/70">
            {t('PAGES.API_INTEGRATION.EMPTY_CANVAS_SUBTITLE')}
          </p>
        </div>
      )}
    </div>
  );
};
